import { useCallback, useEffect, useState } from "react";
import { quoteHubwayzCustomerPrice, quoteHubwayzHotel } from "../../../services/hubwayzHotelsApi";
import { buildHubwayzCustomerPriceRequest, serverQuotePriceView } from "./hubwayzVerifiedQuote";

async function requestVerifiedQuote(claim) {
  const customer = buildHubwayzCustomerPriceRequest(claim);
  if (customer?.commissionType) return quoteHubwayzCustomerPrice(claim);
  return quoteHubwayzHotel(claim);
}

/**
 * One server quote per offer. Changing the room, commission, or offer
 * changes the claim key and requests a new quote.
 */
export function useHubwayzVerifiedQuotes({ enabled = false, claims = [], refreshKey = "" } = {}) {
  const claimKey = JSON.stringify(claims || []);
  const [loading, setLoading] = useState(false);
  const [byCode, setByCode] = useState({});

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setByCode({});
      return undefined;
    }
    let parsed = [];
    try {
      parsed = JSON.parse(claimKey);
    } catch {
      parsed = [];
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      setLoading(false);
      setByCode({});
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      parsed.map(async (claim) => {
        const code = String(claim?.offerCode || "");
        try {
          const data = await requestVerifiedQuote(claim);
          return [code, serverQuotePriceView(data)];
        } catch (err) {
          return [code, serverQuotePriceView(err?.response?.data)];
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      setByCode(Object.fromEntries(rows));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, claimKey, refreshKey]);

  const acknowledge = useCallback(
    async (offerCode) => {
      let parsed = [];
      try {
        parsed = JSON.parse(claimKey);
      } catch {
        parsed = [];
      }
      const claim = parsed.find((row) => String(row?.offerCode || "") === String(offerCode || ""));
      if (!claim) return null;
      setLoading(true);
      try {
        const data = await requestVerifiedQuote({ ...claim, acknowledgePriceChange: true });
        const view = serverQuotePriceView(data);
        setByCode((prev) => ({ ...prev, [String(offerCode)]: view }));
        return view;
      } catch (err) {
        const view = serverQuotePriceView(err?.response?.data);
        setByCode((prev) => ({ ...prev, [String(offerCode)]: view }));
        return view;
      } finally {
        setLoading(false);
      }
    },
    [claimKey]
  );

  return { loading, byCode, acknowledge };
}
